import React, { useState } from "react";
import { Link } from "react-router-dom";
import { Plus, Minus, MessageCircle, ArrowRight } from "lucide-react";
import usePageMeta from "../hooks/usePageMeta";
import Breadcrumbs from "../components/Breadcrumbs";
import { COMPANY, PORTFOLIO } from "../content";

/**
 * Reusable AISO + SEO landing page template for high-intent service keywords.
 *
 * Each landing page (e.g. /villa-renovation-dubai, /office-fit-out-dubai)
 * supplies content via props — the layout, schema, and styling stay consistent.
 */
export default function ServiceLandingPage({
  testid,
  pageTitle,
  metaDescription,
  canonical,
  ogImage = "/media/team-projects/time-furniture-finishing-installation-team.webp",
  serviceSchemaName,
  breadcrumbLabel,
  eyebrow,
  h1,
  h1Accent,
  intro,
  scope,
  process,
  whyChoose,
  faqs,
  ctaHeading,
  ctaAccent,
  ctaSubtext,
  whatsappText = COMPANY.whatsappFitOutText,
  internalLinksLine,
  // Optional slot rendered between "Why Choose" and "Portfolio Preview".
  // Used on Kitchen, Wardrobes and joinery-related pages to surface
  // hardware & appliance brand authority. Pass any JSX (e.g. one or more
  // <BrandLogosSection /> instances).
  brandSections = null,
  // Optional editorial hero image (full-bleed, behind hero copy with a
  // dark gradient for legibility). When omitted, the warm cream hero
  // remains as before.
  heroImage = null,
  heroImageAlt = "",
  // Optional JSX media node rendered in place of the hero <img> (e.g. a
  // MotionMedia video hero). Gradient + hero copy overlay are preserved.
  heroMedia = null,
  // Optional JSX slot rendered between "Scope" and "Process". Used for
  // visual feature grids ("Kitchen Details We Handle", "Storage Designed
  // Around Your Lifestyle", etc.).
  featureGrid = null,
  // Optional JSX slot rendered between intro and Scope. Used for AISO
  // (AI Search Optimization) — Who This Is For, What We Actually Do,
  // Typical Scope, and related-services cross-links.
  aisoBlock = null,
  // Rendered immediately after the hero, before the introduction.
  afterHero = null,
  // Extra JSON-LD blocks (e.g. VideoObject for cinematic heroes).
  extraJsonLd = [],
  // Full hero replacement (e.g. CinematicServiceHero). All other sections render as usual.
  heroOverride = null,
}) {
  const [openFaq, setOpenFaq] = useState(0);

  usePageMeta({
    title: pageTitle,
    description: metaDescription,
    canonical,
    ogImage,
    jsonLd: [
      {
        "@context": "https://schema.org",
        "@type": "WebPage",
        url: canonical,
        name: pageTitle,
        description: metaDescription,
        mainEntity: {
          "@type": "Service",
          name: serviceSchemaName,
          provider: { "@id": "https://timefurniture.ae/#organization" },
          areaServed: ["Dubai", "Sharjah", "Abu Dhabi", "United Arab Emirates"],
          serviceType: scope.map((s) => s.name),
        },
      },
      {
        "@context": "https://schema.org",
        "@type": "FAQPage",
        mainEntity: faqs.map((f) => ({
          "@type": "Question",
          name: f.q,
          acceptedAnswer: { "@type": "Answer", text: f.a },
        })),
      },
      ...extraJsonLd.filter(Boolean),
    ],
  });

  const whatsappHref = `https://wa.me/${COMPANY.whatsapp}?text=${whatsappText}`;
  // Show 3 most recent portfolio items as the preview block.
  const portfolioPreview = PORTFOLIO.slice(0, 3);

  return (
    <div data-testid={testid} className="bg-[#FAF5EC]">
      <Breadcrumbs
        padTop={heroOverride ? "pt-6 md:pt-8" : undefined}
        items={[{ label: "Home", to: "/" }, { label: breadcrumbLabel }]}
      />

      {/* Hero — editorial image variant when heroImage is provided */}
      {heroOverride || (heroImage ? (
        <section
          data-testid={`${testid}-hero`}
          className="relative -mt-px"
        >
          <div className="relative h-[68vh] min-h-[520px] md:h-[78vh] md:min-h-[640px] w-full overflow-hidden">
            {heroMedia || (
              <img
                src={heroImage}
                alt={heroImageAlt || `${h1} ${h1Accent}`.trim()}
                loading="eager"
                className="absolute inset-0 w-full h-full object-cover"
              />
            )}
            <div className="absolute inset-0 bg-gradient-to-b from-[#1C110C]/35 via-[#1C110C]/40 to-[#1C110C]/85 pointer-events-none" />
            <div className="absolute inset-x-0 bottom-0">
              <div className="max-w-[1200px] mx-auto px-6 md:px-12 pb-14 md:pb-20">
                <p className="text-[10px] tracking-[0.32em] uppercase text-[#D4A963] mb-5">
                  {eyebrow}
                </p>
                <h1
                  data-testid={`${testid}-h1`}
                  className="font-serif-display text-4xl md:text-6xl lg:text-[72px] leading-[1.04] tracking-tight text-[#FAF5EC] max-w-4xl"
                >
                  {h1}{" "}
                  <em className="text-[#D4A963] not-italic">{h1Accent}</em>.
                </h1>
                <p
                  data-testid={`${testid}-hero-subtext`}
                  className="mt-7 max-w-2xl text-[#EDE1C8]/90 leading-[1.7] text-base md:text-lg font-light"
                >
                  Premium turnkey execution since 1993 · UAE-wide service · One contract, one accountable team.
                </p>
                <div className="mt-9 flex flex-col sm:flex-row gap-4">
                  <Link
                    to="/contact"
                    data-testid={`${testid}-hero-cta-quote`}
                    className="inline-flex items-center justify-center gap-3 bg-[#D4A963] text-[#1C110C] px-9 py-4 text-[11px] tracking-[0.32em] uppercase font-medium hover:bg-[#c9a166] transition-colors"
                  >
                    Get Quote <ArrowRight size={14} />
                  </Link>
                  <a
                    href={whatsappHref}
                    target="_blank"
                    rel="noopener noreferrer"
                    data-testid={`${testid}-hero-cta-whatsapp`}
                    className="inline-flex items-center justify-center gap-3 border border-[#FAF5EC]/55 text-[#FAF5EC] px-9 py-4 text-[11px] tracking-[0.32em] uppercase hover:bg-[#FAF5EC] hover:text-[#1C110C] transition-colors"
                  >
                    <MessageCircle size={14} strokeWidth={2.2} /> WhatsApp Us
                  </a>
                </div>
              </div>
            </div>
          </div>
        </section>
      ) : (
        <section className="max-w-[1100px] mx-auto px-6 md:px-12 pt-10 md:pt-14 pb-16 md:pb-20">
          <p className="eyebrow mb-5">{eyebrow}</p>
          <h1
            data-testid={`${testid}-h1`}
            className="font-serif-display text-4xl md:text-6xl lg:text-[68px] leading-[1.05] tracking-tight text-[#1C110C]"
          >
            {h1}{" "}
            <em className="text-[#6E1F2C] not-italic">{h1Accent}</em>.
          </h1>
          <p
            data-testid={`${testid}-hero-subtext`}
            className="mt-8 max-w-2xl text-[#3E2A22] leading-[1.7] text-base md:text-lg font-light"
          >
            Premium turnkey execution since 1993 · UAE-wide service · One contract, one accountable team.
          </p>
          <div className="mt-10 flex flex-col sm:flex-row gap-4">
            <Link
              to="/contact"
              data-testid={`${testid}-hero-cta-quote`}
              className="inline-flex items-center justify-center gap-3 bg-[#6E1F2C] text-white px-9 py-4 text-[11px] tracking-[0.32em] uppercase hover:bg-[#56151F] transition-colors"
            >
              Get Quote <ArrowRight size={14} />
            </Link>
            <a
              href={whatsappHref}
              target="_blank"
              rel="noopener noreferrer"
              data-testid={`${testid}-hero-cta-whatsapp`}
              className="inline-flex items-center justify-center gap-3 border border-[#6E1F2C] text-[#6E1F2C] px-9 py-4 text-[11px] tracking-[0.32em] uppercase hover:bg-[#6E1F2C] hover:text-white transition-colors"
            >
              <MessageCircle size={14} strokeWidth={2.2} /> WhatsApp Us
            </a>
          </div>
        </section>
      ))}

      {afterHero}

      {/* 1. Introduction */}
      <section
        data-testid={`${testid}-intro`}
        className="max-w-[1100px] mx-auto px-6 md:px-12 pb-20 md:pb-28"
      >
        <h2 className="font-serif-display text-2xl md:text-3xl text-[#1C110C] mb-5">
          Introduction
        </h2>
        <p className="text-[#3E2A22] leading-[1.85] text-base md:text-[17px] max-w-3xl">
          {intro}
        </p>
      </section>

      {/* 2. Scope of Work */}
      <section
        data-testid={`${testid}-scope`}
        className="bg-[#F2E9D8] border-y border-[#6E1F2C]/15"
      >
        <div className="max-w-[1100px] mx-auto px-6 md:px-12 py-20 md:py-28">
          <h2 className="font-serif-display text-2xl md:text-3xl text-[#1C110C] mb-10">
            Scope of work
          </h2>
          <div className="grid grid-cols-1 md:grid-cols-2 gap-8 md:gap-12">
            {scope.map((s, i) => (
              <div
                key={s.name}
                data-testid={`${testid}-scope-${i}`}
                className="border-t border-[#6E1F2C]/20 pt-6"
              >
                <p className="text-[10px] tracking-[0.32em] uppercase text-[#D4A963] mb-3">
                  {String(i + 1).padStart(2, "0")}
                </p>
                <h3 className="font-serif-display text-xl md:text-2xl text-[#1C110C] mb-3">
                  {s.name}
                </h3>
                <p className="text-[#3E2A22] leading-[1.8] text-base">
                  {s.desc}
                </p>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* 2.5 · Optional editorial feature grid (kitchen details, wardrobe types, etc.) */}
      {featureGrid}

      {/* 2.6 · Optional AISO block — "Who This Is For", "What We Actually Do",
              "Typical Scope" and related-services cross-links. */}
      {aisoBlock}

      {/* 3. Process */}
      <section
        data-testid={`${testid}-process`}
        className="max-w-[1200px] mx-auto px-6 md:px-12 py-20 md:py-28"
      >
        <h2 className="font-serif-display text-2xl md:text-3xl text-[#1C110C] mb-10">
          How we work — step by step
        </h2>
        <ol className="space-y-12 md:space-y-16">
          {process.map((s) => (
            <li
              key={s.n}
              data-testid={`${testid}-step-${s.n}`}
              className="border-t border-[#6E1F2C]/15 pt-8"
            >
              <div className="grid grid-cols-12 gap-6 md:gap-10">
                <p className="col-span-2 md:col-span-1 font-serif-display text-2xl md:text-3xl text-[#D4A963]">
                  {s.n}
                </p>
                <div className="col-span-10 md:col-span-11 grid grid-cols-1 md:grid-cols-12 gap-6 md:gap-10 items-start">
                  <div className={s.image ? "md:col-span-7" : "md:col-span-12"}>
                    <h3 className="font-serif-display text-xl md:text-2xl text-[#1C110C] mb-3">
                      {s.title}
                    </h3>
                    <p className="text-[#3E2A22] leading-[1.85] text-base md:text-[17px] max-w-2xl">
                      {s.body}
                    </p>
                  </div>
                  {s.image && (
                    <div className="md:col-span-5 order-first md:order-none">
                      <div className="aspect-[5/4] overflow-hidden bg-[#1C110C]/[0.04]">
                        <img
                          src={s.image}
                          alt={s.imageAlt || s.title}
                          loading="lazy"
                          className="w-full h-full object-cover"
                        />
                      </div>
                    </div>
                  )}
                </div>
              </div>
            </li>
          ))}
        </ol>
      </section>

      {/* 4. Why Choose Us */}
      <section
        data-testid={`${testid}-why`}
        className="bg-[#F2E9D8] border-y border-[#6E1F2C]/15"
      >
        <div className="max-w-[1100px] mx-auto px-6 md:px-12 py-20 md:py-28">
          <h2 className="font-serif-display text-2xl md:text-3xl text-[#1C110C] mb-10">
            Why choose Time Furniture
          </h2>
          <div className="grid grid-cols-1 md:grid-cols-3 gap-10">
            {whyChoose.map((w) => (
              <div key={w.t} className="border-t border-[#6E1F2C]/20 pt-6">
                <h3 className="font-serif-display text-xl md:text-2xl text-[#1C110C] mb-3">
                  {w.t}
                </h3>
                <p className="text-[#3E2A22] leading-[1.8] text-base">
                  {w.d}
                </p>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* 4.5 · Brand authority (optional — kitchen/wardrobes/joinery only) */}
      {brandSections}

      {/* 4.5 · Portfolio Preview */}
      <section
        data-testid={`${testid}-portfolio-preview`}
        className="max-w-[1100px] mx-auto px-6 md:px-12 py-20 md:py-28"
      >
        <div className="flex items-end justify-between mb-10 md:mb-12 flex-wrap gap-6">
          <div>
            <p className="eyebrow mb-4">Recent Projects</p>
            <h2 className="font-serif-display text-2xl md:text-3xl text-[#1C110C]">
              Selected work across the UAE
            </h2>
          </div>
          <Link
            to="/portfolio"
            data-testid={`${testid}-portfolio-link`}
            className="text-[10px] tracking-[0.32em] uppercase text-[#6E1F2C] border-b border-[#6E1F2C]/40 pb-1 hover:border-[#6E1F2C]"
          >
            View Full Portfolio →
          </Link>
        </div>
        <div className="grid grid-cols-1 md:grid-cols-3 gap-6 md:gap-8">
          {portfolioPreview.map((p, i) => (
            <Link
              key={p.id}
              to={`/portfolio/${p.id}`}
              data-testid={`${testid}-portfolio-card-${i}`}
              className="group block"
            >
              <div className="aspect-[4/5] overflow-hidden bg-[#1C110C]/5">
                <img
                  src={p.image}
                  alt={`${p.title} — ${p.location}`}
                  loading="lazy"
                  className="w-full h-full object-cover group-hover:scale-[1.03] transition-transform duration-700"
                />
              </div>
              <p className="mt-4 text-[10px] tracking-[0.32em] uppercase text-[#7A6B5F]">
                {p.category} · {p.year}
              </p>
              <h3 className="mt-2 font-serif-display text-lg text-[#1C110C] group-hover:text-[#6E1F2C] transition-colors">
                {p.title}
              </h3>
              <p className="mt-1 text-sm text-[#7A6B5F]">{p.location}</p>
            </Link>
          ))}
        </div>
      </section>

      {/* 5. FAQ */}
      <section
        data-testid={`${testid}-faq`}
        className="max-w-[1100px] mx-auto px-6 md:px-12 py-20 md:py-28"
      >
        <p className="eyebrow mb-5">Common Questions</p>
        <h2 className="font-serif-display text-2xl md:text-3xl text-[#1C110C] mb-10">
          Frequently asked
        </h2>
        <div className="border-t border-[#6E1F2C]/20">
          {faqs.map((f, i) => {
            const isOpen = openFaq === i;
            return (
              <div
                key={f.q}
                data-testid={`${testid}-faq-item-${i}`}
                className="border-b border-[#6E1F2C]/20"
              >
                <button
                  type="button"
                  onClick={() => setOpenFaq(isOpen ? -1 : i)}
                  data-testid={`${testid}-faq-toggle-${i}`}
                  className="w-full flex items-center justify-between gap-6 py-7 text-left"
                  aria-expanded={isOpen}
                >
                  <span className="font-serif-display text-lg md:text-2xl text-[#1C110C] leading-snug">
                    {f.q}
                  </span>
                  <span className="text-[#6E1F2C] flex-shrink-0">
                    {isOpen ? <Minus size={20} /> : <Plus size={20} />}
                  </span>
                </button>
                {isOpen && (
                  <p className="pb-8 pr-12 text-[#3E2A22] leading-[1.85] text-base md:text-[17px]">
                    {f.a}
                  </p>
                )}
              </div>
            );
          })}
        </div>
      </section>

      {/* CTA */}
      <section
        data-testid={`${testid}-cta`}
        className="bg-[#1C110C] text-[#FAF5EC]"
      >
        <div className="max-w-[1100px] mx-auto px-6 md:px-12 py-20 md:py-28 text-center">
          <h2 className="font-serif-display text-3xl md:text-5xl tracking-tight leading-[1.08] text-[#FAF5EC] max-w-3xl mx-auto">
            {ctaHeading}{" "}
            <em className="text-[#D4A963] not-italic">{ctaAccent}</em>?
          </h2>
          <p className="mt-7 max-w-xl mx-auto text-[#EDE1C8]/85 leading-[1.7] text-base md:text-lg">
            {ctaSubtext}
          </p>
          <div className="mt-10 flex flex-col sm:flex-row gap-4 justify-center">
            <a
              href={whatsappHref}
              target="_blank"
              rel="noopener noreferrer"
              data-testid={`${testid}-cta-whatsapp`}
              className="inline-flex items-center justify-center gap-3 bg-[#D4A963] text-[#1C110C] px-10 py-5 text-[11px] tracking-[0.32em] uppercase font-medium hover:bg-[#c9a166] transition-all"
            >
              <MessageCircle size={14} strokeWidth={2.2} /> WhatsApp Us →
            </a>
            <Link
              to="/contact"
              data-testid={`${testid}-cta-inquiry`}
              className="inline-flex items-center justify-center gap-3 border border-[#D4A963]/60 text-[#FAF5EC] px-10 py-5 text-[11px] tracking-[0.32em] uppercase font-medium hover:bg-[#D4A963] hover:text-[#1C110C] hover:border-[#D4A963] transition-all"
            >
              Send Inquiry Form
            </Link>
          </div>
          <p className="mt-8 text-xs tracking-[0.04em] text-[#EDE1C8]/60">
            Crafted since 1993 · Dubai · Sharjah · Abu Dhabi · UAE
          </p>
        </div>
      </section>

      {/* Internal links */}
      <section className="max-w-[1100px] mx-auto px-6 md:px-12 py-12 text-center">
        <p className="text-sm text-[#7A6B5F]">{internalLinksLine}</p>
      </section>
    </div>
  );
}

/**
 * Helper for assembling internal links inside `internalLinksLine`.
 * Usage: <InternalLink to="/services">full fit-out services</InternalLink>
 */
export const InternalLink = ({ to, children }) => (
  <Link
    to={to}
    className="text-[#6E1F2C] underline underline-offset-4 hover:text-[#56151F]"
  >
    {children}
  </Link>
);
